import { config } from "JS/Config";
import { buildUrlWithQueryParams } from "JS/Helpers";
import { AuthorizedRoutingContext } from "./Context/AuthorizedRoutingContext";
import { BaseRoutingContext } from "./Context/BaseRoutingContext";

export const routesForContext = () => () => {
    const authorizedContext = new AuthorizedRoutingContext();
    const unauthorizedContext = new BaseRoutingContext();

    return {
        react: {
            root: () => unauthorizedContext.buildUrl(`/`),
            login: (historyIndex?: string) =>
                historyIndex
                    ? unauthorizedContext.buildUrl(
                          `/login?history_index=${historyIndex}`,
                      )
                    : unauthorizedContext.buildUrl("/login"),

            userHistory: () => unauthorizedContext.buildUrl(`/userHistory`),

            home: () => authorizedContext.buildUrl(`/home`),
            attachedSku: () => authorizedContext.buildUrl(`/attached_sku`),
            videos: () => ({
                index: () => authorizedContext.buildUrl(`/videos`),
                detail: (
                    id: string = ":contentId",
                    categoryId: string = ":categoryId",
                    isAutoPlay: string = ":isAutoPlay",
                    queryParams?: {
                        scrollTo?: number;
                    },
                ) => {
                    const url = `/videos/${id}/${categoryId}/detail/${isAutoPlay}`;
                    return authorizedContext.buildUrl(
                        queryParams
                            ? buildUrlWithQueryParams(url, queryParams)
                            : url,
                    );
                },
                listing: (
                    id: string = ":categoryId",
                    queryParams?: {
                        scrollTo?: number;
                    },
                ) => {
                    const url = `/videos/${id}/listings`;
                    return authorizedContext.buildUrl(
                        queryParams
                            ? buildUrlWithQueryParams(url, queryParams)
                            : url,
                    );
                },
            }),
            audios: () => ({
                index: (queryParams?: { scrollTo?: number }) => {
                    const url = `/audios`;
                    return authorizedContext.buildUrl(
                        queryParams
                            ? buildUrlWithQueryParams(url, queryParams)
                            : url,
                    );
                },
                detail: (
                    id: string = ":contentId",
                    categoryId: string = ":categoryId",
                    isAutoPlay: string = ":isAutoPlay",
                    queryParams?: {
                        recId?: number;
                        displayCount?: number;
                        source?: string;
                        scrollTo?: number;
                    },
                ) => {
                    isAutoPlay = "false";
                    const url = `/audios/${id}/${categoryId}/detail/${isAutoPlay}`;
                    return authorizedContext.buildUrl(
                        queryParams
                            ? buildUrlWithQueryParams(url, queryParams)
                            : url,
                    );
                },
                listing: (
                    id: string = ":categoryId",
                    queryParams?: {
                        scrollTo?: number;
                    },
                ) => {
                    const url = `/audios/${id}/listings`;
                    return authorizedContext.buildUrl(
                        queryParams
                            ? buildUrlWithQueryParams(url, queryParams)
                            : url,
                    );
                },
            }),
            bundle: () => ({
                audios: (
                    id: String = ":contentId",
                    categoryId: string = ":categoryId",
                    isGift: string = ":isGift",
                    queryParams?: {
                        scrollTo?: number;
                    },
                ) => {
                    const url = categoryId
                        ? `/bundle/${id}/audios/${isGift}/${categoryId}`
                        : `/bundle/${id}/audios/${isGift}`;

                    return authorizedContext.buildUrl(
                        queryParams
                            ? buildUrlWithQueryParams(url, queryParams)
                            : url,
                    );
                },
                play: (
                    id: string = ":contentId",
                    queryParams?: {
                        categoryId?: string;
                        isGift?: string;
                        index?: string;
                        scrollTo?: number;
                    },
                ) => {
                    const url = `/bundle/${id}/play`;
                    // isGift=${isGift}&index=${index}&categoryId=${categoryId}
                    return authorizedContext.buildUrl(
                        queryParams
                            ? buildUrlWithQueryParams(url, queryParams)
                            : url,
                    );
                },
            }),
            courses: () => ({
                index: (queryParams?: {
                    segment?: string;
                    scrollTo?: number;
                }) => {
                    const url = `/courses`;
                    return authorizedContext.buildUrl(
                        queryParams
                            ? buildUrlWithQueryParams(url, queryParams)
                            : url,
                    );
                },
                detail: (
                    id: string = ":id",
                    queryParams?: {
                        received?: string;
                        segment?: string;
                        scrollTo?: number;
                    },
                ) => {
                    const url = `/courses/${id}/detail`;
                    return authorizedContext.buildUrl(
                        queryParams
                            ? buildUrlWithQueryParams(url, queryParams)
                            : url,
                    );
                },
                mediaStepDetail: (
                    detailSkuId: string = ":detailSkuId",
                    stepSkuId: string = ":stepSkuId",
                    isAutoPlay: string = ":isAutoPlay",
                    queryParams?: {
                        displayMode?: boolean;
                        segment?: string;
                        scrollTo?: string;
                    },
                ) => {
                    const url = `/courses/detail/${detailSkuId}/step/${stepSkuId}/${isAutoPlay}/media`;
                    return authorizedContext.buildUrl(
                        queryParams
                            ? buildUrlWithQueryParams(url, queryParams)
                            : url,
                    );
                },
            }),
            mixedContent: () => ({
                index: (
                    filePath?: string,
                    queryParams?: {
                        scrollTo: string;
                    },
                ) => {
                    const url = authorizedContext.buildUrl(`/mixedContent`);
                    const allParams = {
                        ...(filePath ? { filePath } : {}),
                        ...queryParams,
                    };
                    if (Object.keys(allParams).length > 0)
                        return buildUrlWithQueryParams(url, allParams);
                    return url;
                },
                audioDetail: (
                    id: string = ":contentId",
                    categoryId: string = ":categoryId",
                    isAutoPlay: string = ":isAutoPlay",
                    queryParams?: {
                        isMixedContent: boolean;
                        scrollTo: string;
                        filePath?: string;
                    },
                ) => {
                    isAutoPlay = "false";
                    const url = `/mixedContent/audios/${id}/${categoryId}/detail/${isAutoPlay}`;
                    return authorizedContext.buildUrl(
                        queryParams
                            ? buildUrlWithQueryParams(url, queryParams)
                            : url,
                    );
                },
                videoDetail: (
                    id: string = ":contentId",
                    categoryId: string = ":categoryId",
                    isAutoPlay: string = ":isAutoPlay",
                    queryParams?: {
                        isMixedContent: boolean;
                        scrollTo: string;
                        filePath?: string;
                    },
                ) => {
                    const url = `/mixedContent/videos/${id}/${categoryId}/detail/${isAutoPlay}`;
                    return authorizedContext.buildUrl(
                        queryParams
                            ? buildUrlWithQueryParams(url, queryParams)
                            : url,
                    );
                },
                bundleDetail: (
                    id: String = ":contentId",
                    categoryId: string = ":categoryId",
                    isGift: string = ":isGift",
                    queryParams?: {
                        isMixedContent: boolean;
                        scrollTo: string;
                        filePath?: string;
                    },
                ) => {
                    let url = ``;
                    if (categoryId) {
                        url = `/mixedContent/bundle/${id}/audios/${isGift}/${categoryId}`;
                    } else {
                        url = `/mixedContent/bundle/${id}/audios/${isGift}`;
                    }
                    return authorizedContext.buildUrl(
                        queryParams
                            ? buildUrlWithQueryParams(url, queryParams)
                            : url,
                    );
                },
                bundlePlay: (
                    id: string = ":contentId",
                    categoryId: string = ":categoryId",
                    isGift?: string,
                    index?: string,
                    queryParams?: {
                        isMixedContent: boolean;
                        scrollTo: string;
                        filePath?: string;
                    },
                ) => {
                    let url = "";
                    if (isGift && index && categoryId) {
                        url = `/mixedContent/bundle/${id}/play?isGift=${isGift}&index=${index}&categoryId=${categoryId}`;
                    } else {
                        if (index && categoryId) {
                            url = `/mixedContent/bundle/${id}/play?index=${index}&categoryId=${categoryId}`;
                        } else {
                            if (isGift && index) {
                                url = `/mixedContent/bundle/${id}/play?isGift=${isGift}&index=${index}`;
                            } else {
                                url = `/mixedContent/bundle/${id}/play`;
                            }
                        }
                    }
                    return authorizedContext.buildUrl(
                        queryParams
                            ? buildUrlWithQueryParams(url, queryParams)
                            : url,
                    );
                },
                courseDetail: (
                    id: string = ":id",
                    received?: string,
                    contentType?: string,
                    queryParams?: {
                        isMixedContent: boolean;
                        scrollTo: string;
                        filePath?: string;
                    },
                ) => {
                    let url = "";
                    if (received && contentType) {
                        url = `/mixedContent/courses/${id}/detail?received=${received}&segment=${contentType}`;
                    } else {
                        if (received) {
                            url = `/mixedContent/courses/${id}/detail?received=${received}`;
                        } else {
                            url = `/mixedContent/courses/${id}/detail`;
                        }
                    }
                    return authorizedContext.buildUrl(
                        queryParams
                            ? buildUrlWithQueryParams(url, queryParams)
                            : url,
                    );
                },
                courseMediaStepDetail: (
                    detailSkuId: string = ":detailSkuId",
                    stepSkuId: string = ":stepSkuId",
                    isAutoPlay: string = ":isAutoPlay",
                    displayMode?: boolean,
                    contentType?: string,
                    queryParams?: {
                        isMixedContent: boolean;
                        scrollTo: number;
                        filePath?: string;
                    },
                ) => {
                    let url = "";
                    if (displayMode && contentType) {
                        url = `/mixedContent/courses/detail/${detailSkuId}/step/${stepSkuId}/${isAutoPlay}/media?displayMode=${displayMode}&segment=${contentType}`;
                    } else {
                        if (displayMode) {
                            url = `/mixedContent/courses/detail/${detailSkuId}/step/${stepSkuId}/${isAutoPlay}/media?displayMode=${displayMode}`;
                        } else {
                            url = `/mixedContent/courses/detail/${detailSkuId}/step/${stepSkuId}/${isAutoPlay}/media`;
                        }
                    }
                    return authorizedContext.buildUrl(
                        queryParams
                            ? buildUrlWithQueryParams(url, queryParams)
                            : url,
                    );
                },
            }),
            gifts: () => ({
                index: () => authorizedContext.buildUrl(`/gifts`),
                listing: (
                    id: string = ":categoryId",
                    selectedTab: string = ":selectedTab",
                    queryParams?: {
                        scrollTo: string;
                    },
                ) => {
                    const url = `/gifts/${id}/${selectedTab}/listings`;
                    return queryParams
                        ? buildUrlWithQueryParams(url, queryParams)
                        : authorizedContext.buildUrl(url);
                },
                detail: (
                    id: string = ":contentId",
                    categoryId: string = ":categoryId",
                    selectedTab: string = ":selectedTab",
                    queryParams?: {
                        scrollTo: string;
                    },
                ) => {
                    const url = `/gifts/${categoryId}/${selectedTab}/${id}/detail`;
                    return queryParams
                        ? buildUrlWithQueryParams(url, queryParams)
                        : authorizedContext.buildUrl(url);
                },
                giftAudioDetail: (
                    id: string = ":contentId",
                    categoryId: string = ":categoryId",
                    selectedTab: string = ":selectedTab",
                    isAutoPlay: string = ":isAutoPlay",
                    queryParams?: {
                        scrollTo: string;
                    },
                ) => {
                    const url = `/received/audios/${id}/detail/${categoryId}/${selectedTab}/${isAutoPlay}`;
                    return queryParams
                        ? buildUrlWithQueryParams(url, queryParams)
                        : authorizedContext.buildUrl(url);
                },
                giftsGivenListing: (
                    categoryId: string = ":categoryId",
                    queryParams?: {
                        scrollTo: number;
                    },
                ) => {
                    const url = `/gifts-given/listings/${categoryId}`;
                    return queryParams
                        ? buildUrlWithQueryParams(url, queryParams)
                        : authorizedContext.buildUrl(url);
                },
                giftsGivenContentListing: (
                    memberId: string = ":memberId",
                    selectedTab: string = ":selectedTab",
                    categoryId: string = ":categoryId",
                    queryParams?: {
                        scrollTo?: number;
                    },
                ) => {
                    const url = categoryId
                        ? `/gifts-given/${memberId}/${selectedTab}/listing/${categoryId}`
                        : `/gifts-given/${memberId}/${selectedTab}/listing`;
                    return queryParams
                        ? buildUrlWithQueryParams(url, queryParams)
                        : authorizedContext.buildUrl(url);
                },
                sync: () => `/sync`,
                courseDetail: (
                    id: string = ":id",
                    queryParams: {
                        received?: string;
                        giftTab?: string;
                        contentType?: string;
                        scrollTo?: string;
                    } = null,
                ) => {
                    const url = `/gift/courses/${id}/detail`;
                    return queryParams
                        ? buildUrlWithQueryParams(url, queryParams)
                        : authorizedContext.buildUrl(url);
                },
                courseMediaStepDetail: (
                    courseNid: string = ":courseNid",
                    detailSkuId: string = ":detailSkuId",
                    stepSkuId: string = ":stepSkuId",
                    isAutoPlay: string = ":isAutoPlay",
                    displayMode?: boolean,
                    contentType?: string,
                    giftTab?: string,
                    queryParams?: {
                        scrollTo: string;
                    },
                ) => {
                    const commonUrl = `/gift/courses/${courseNid}/detail/${detailSkuId}/step/${stepSkuId}/${isAutoPlay}/media`;
                    const url =
                        displayMode && contentType
                            ? `${commonUrl}?displayMode=${displayMode}&segment=${contentType}&giftTab=${giftTab}`
                            : displayMode && giftTab
                            ? `${commonUrl}?displayMode=${displayMode}&giftTab=${giftTab}`
                            : giftTab
                            ? `${commonUrl}?giftTab=${giftTab}`
                            : commonUrl;

                    return authorizedContext.buildUrl(
                        queryParams
                            ? buildUrlWithQueryParams(url, queryParams)
                            : url,
                    );
                },
            }),
            more: () => ({
                index: () => authorizedContext.buildUrl(`/more`),
            }),
            events: () => ({
                index: () => authorizedContext.buildUrl(`/events`),
                detail: (
                    eventName: string = ":eventName",
                    selectedTab: string = ":selectedTab",
                ) =>
                    selectedTab
                        ? authorizedContext.buildUrl(
                              `/events/${eventName}/detail/${selectedTab}`,
                          )
                        : authorizedContext.buildUrl(
                              `/events/${eventName}/detail`,
                          ),
            }),
            fycStatus: () => ({
                index: (selectedTab: string = ":user") =>
                    selectedTab
                        ? authorizedContext.buildUrl(
                              `/fyc_status/${selectedTab}`,
                          )
                        : authorizedContext.buildUrl(
                              `/fyc_status/primary_user`,
                          ),
            }),
            feedback: () => ({
                index: () => authorizedContext.buildUrl(`/feedback`),
            }),
            documents: () => ({
                index: () => authorizedContext.buildUrl(`/documents/index`),
                detail: (
                    pdfName: string = ":pdf",
                    description: string = ":description",
                    isFavoriteTab: string = ":isFavoriteTab",
                ) =>
                    isFavoriteTab
                        ? authorizedContext.buildUrl(
                              `/documents/detail/${isFavoriteTab}/${description}/${pdfName}`,
                          )
                        : authorizedContext.buildUrl(
                              `/documents/detail/${description}/${pdfName}`,
                          ),
            }),

            webcast: () => ({
                index: (backNav?: boolean) =>
                    backNav
                        ? authorizedContext.buildUrl(
                              `/webcast?backNav=${backNav}`,
                          )
                        : authorizedContext.buildUrl(`/webcast`),
                detail: (title: String = ":title", backNav?: boolean) =>
                    backNav
                        ? authorizedContext.buildUrl(
                              `/webcast/${title}/detail?backNav=${backNav}`,
                          )
                        : authorizedContext.buildUrl(
                              `/webcast/${title}/detail`,
                          ),
                stream: (title: String = ":title", backNav?: boolean) =>
                    backNav
                        ? authorizedContext.buildUrl(
                              `/webcast/${title}/stream?backNav=${backNav}`,
                          )
                        : authorizedContext.buildUrl(
                              `/webcast/${title}/stream`,
                          ),
            }),
            appInfo: () => ({
                index: () => authorizedContext.buildUrl(`/appinfo`),
            }),
            sso: () => ({
                index: () => authorizedContext.buildUrl("/sso"),
            }),
            offline: () => ({
                index: () => authorizedContext.buildUrl(`/downloads`),
                attachedSku: () => authorizedContext.buildUrl(`/attached_sku`),
                listing: (
                    id: string = ":category",
                    queryParams?: { scrollTo?: number },
                ) => {
                    const url = `/downloads/${id}`;
                    return authorizedContext.buildUrl(
                        queryParams
                            ? buildUrlWithQueryParams(url, queryParams)
                            : url,
                    );
                },
                playlist: () => ({
                    index: () =>
                        authorizedContext.buildUrl(`/playlist/downloads`),
                    detail: (
                        id: String = ":id",
                        queryParams?: {
                            scrollTo?: string;
                        },
                    ) => {
                        const url = `offline/playlist/detail/${id}`;
                        return authorizedContext.buildUrl(
                            queryParams
                                ? buildUrlWithQueryParams(url, queryParams)
                                : url,
                        );
                    },
                    play: (
                        id: string = ":id",
                        queryParams?: {
                            index?: string;
                            bundleIndex?: string;
                            scrollTo?: string;
                        },
                    ) => {
                        const url = `/playlist/offline/${id}/play`;
                        return authorizedContext.buildUrl(
                            queryParams
                                ? buildUrlWithQueryParams(url, queryParams)
                                : url,
                        );
                    },
                }),
                courses: () => ({
                    index: (queryParams?: { scrollTo?: number }) => {
                        const url = `/courses/downloads`;
                        return authorizedContext.buildUrl(
                            queryParams
                                ? buildUrlWithQueryParams(url, queryParams)
                                : url,
                        );
                    },
                    detail: (
                        id: string = ":id",
                        queryParams?: {
                            scrollTo?: number;
                        },
                    ) => {
                        const url = `/courses/downloads/detail/${id}`;
                        return authorizedContext.buildUrl(
                            queryParams
                                ? buildUrlWithQueryParams(url, queryParams)
                                : url,
                        );
                    },
                    mediaStepDetail: (
                        detailSkuId: string = ":detailSkuId",
                        stepSkuId: string = ":stepSkuId",
                        isAutoPlay: string = ":isAutoPlay",
                        displayMode?: boolean,
                    ) => {
                        return displayMode
                            ? authorizedContext.buildUrl(
                                  `/courses/downloads/detail/${detailSkuId}/step/${stepSkuId}/media/${isAutoPlay}?displayMode=${displayMode}`,
                              )
                            : authorizedContext.buildUrl(
                                  `/courses/downloads/detail/${detailSkuId}/step/${stepSkuId}/media/${isAutoPlay}`,
                              );
                    },
                }),
                documents: () => ({
                    index: () =>
                        authorizedContext.buildUrl(`documents/downloads`),
                    detail: (
                        pdfName: string = ":pdf",
                        description: string = ":description",
                        isFavoriteTab: string = ":isFavoriteTab",
                    ) =>
                        isFavoriteTab
                            ? authorizedContext.buildUrl(
                                  `/documents/downloads/detail/${isFavoriteTab}/${description}/${pdfName}`,
                              )
                            : authorizedContext.buildUrl(
                                  `/documents/downloads/detail/${description}/${pdfName}`,
                              ),
                }),
                downloaddetail: (
                    nid: string = ":nid",
                    type: string = ":type",
                    isAutoPlay: string = ":isAutoPlay",
                    queryParams?: {
                        scrollTo?: number;
                    },
                ) => {
                    const url = `/download-detail/${type}/${nid}/${isAutoPlay}`;
                    return authorizedContext.buildUrl(
                        queryParams
                            ? buildUrlWithQueryParams(url, queryParams)
                            : url,
                    );
                },
                bundle: () => ({
                    audios: (id: String = ":contentId") =>
                        authorizedContext.buildUrl(
                            `/downloaded-bundle/${id}/audios`,
                        ),
                    play: (id: string = ":contentId", index?: string) =>
                        index
                            ? authorizedContext.buildUrl(
                                  `/downloaded-bundle/${id}/play?index=${index}`,
                              )
                            : authorizedContext.buildUrl(
                                  `/downloaded-bundle/${id}/play`,
                              ),
                }),
            }),
            playlist: () => ({
                index: (segment?: string) =>
                    segment
                        ? authorizedContext.buildUrl(
                              `/playlist?segment=${segment}`,
                          )
                        : authorizedContext.buildUrl("/playlist"),
                detail: (
                    id: String = ":id",
                    queryParams?: { scrollTo?: number },
                ) => {
                    const url = `/playlist/${id}/detail`;
                    return authorizedContext.buildUrl(
                        queryParams
                            ? buildUrlWithQueryParams(url, queryParams)
                            : url,
                    );
                },
                play: (
                    id: string = ":id",
                    index?: string,
                    bundleIndex?: string,
                    queryParams?: { scrollTo?: number },
                ) => {
                    const url =
                        id && index && bundleIndex
                            ? `/playlist/${id}/play?index=${index}&bundleIndex=${bundleIndex}`
                            : `/playlist/${id}/play`;

                    return authorizedContext.buildUrl(
                        queryParams
                            ? buildUrlWithQueryParams(url, queryParams)
                            : url,
                    );
                },
            }),
            favorites: () => ({
                index: () => authorizedContext.buildUrl(`/favorites`),
                favoriteAudiosListing: (queryParams?: {
                    scrollTo?: number;
                }) => {
                    const url = `/favorites/Audios/listing`;
                    return authorizedContext.buildUrl(
                        queryParams
                            ? buildUrlWithQueryParams(url, queryParams)
                            : url,
                    );
                },
                favoriteVideosListing: (queryParams?: {
                    scrollTo?: number;
                }) => {
                    const url = `/favorites/Videos/listing`;
                    return authorizedContext.buildUrl(
                        queryParams
                            ? buildUrlWithQueryParams(url, queryParams)
                            : url,
                    );
                },
                favoriteDocumentsListing: () =>
                    authorizedContext.buildUrl(`/favorites/Documents/listing`),
                favoriteVideosDetail: (
                    id: string = ":contentId",
                    categoryId: string = ":categoryId",
                    isFavorite: string = ":isFavorite",
                    isAutoPlay: string = ":isAutoPlay",
                    queryParams?: {
                        scrollTo?: number;
                    },
                ) => {
                    const url = `favorites/videos/${id}/${categoryId}/detail/${isFavorite}/${isAutoPlay}`;
                    return authorizedContext.buildUrl(
                        queryParams
                            ? buildUrlWithQueryParams(url, queryParams)
                            : url,
                    );
                },
                favoriteAudiosDetail: (
                    id: string = ":contentId",
                    categoryId: string = ":categoryId",
                    isFavorite: string = ":isFavorite",
                    isAutoPlay: string = ":isAutoPlay",
                    queryParams?: {
                        scrollTo?: number;
                    },
                ) => {
                    isAutoPlay = "false";
                    const url = `favorites/audios/${id}/${categoryId}/detail/${isFavorite}/${isAutoPlay}`;
                    return authorizedContext.buildUrl(
                        queryParams
                            ? buildUrlWithQueryParams(url, queryParams)
                            : url,
                    );
                },
                favoriteBundleAudios: (
                    id: String = ":contentId",
                    categoryId: string = ":categoryId",
                    isGift: string = ":isGift",
                    queryParams?: {
                        scrollTo?: number;
                    },
                ) => {
                    const url = categoryId
                        ? `/bundle/favorites/${id}/audios/${isGift}/${categoryId}`
                        : `/bundle/favorites/${id}/audios/${isGift}`;
                    if (!!queryParams)
                        return buildUrlWithQueryParams(url, queryParams);
                    return authorizedContext.buildUrl(url);
                },
                favoriteBundlePlay: (
                    id: string = ":contentId",
                    queryParams?: {
                        categoryId: string;
                        isGift?: string;
                        index?: string;
                        scrollTo?: number;
                    },
                ) => {
                    const url = `/bundle/favorites/${id}/play`;
                    if (!!queryParams)
                        return buildUrlWithQueryParams(url, queryParams);
                    return authorizedContext.buildUrl(url);
                },
            }),
            downloadingQueue: () => ({
                index: (contentType?: string) =>
                    contentType
                        ? authorizedContext.buildUrl(
                              `/downloading_queue?contentType=${contentType}`,
                          )
                        : authorizedContext.buildUrl(`/downloading_queue`),
            }),
            recommendations: () => ({
                index: (auto: boolean = false) => {
                    const url = "/recommendations";
                    return authorizedContext.buildUrl(
                        auto ? `${url}?auto=${auto}` : url,
                    );
                },
            }),
            consentForm: () => ({
                index: () => authorizedContext.buildUrl("/consent_form"),
            }),
            checklist: () => ({
                index: () => authorizedContext.buildUrl("/checklist"),
            }),
            appSettings: () => ({
                index: () => {
                    return authorizedContext.buildUrl(`/settings`);
                },
            }),
            giftDashboard: () => ({
                index: () => {
                    return authorizedContext.buildUrl(`/gift_dashboard`);
                },
            }),
            externalSso: () => ({
                index: (resourceKey: string) =>
                    authorizedContext.buildUrl(`/externalSso/${resourceKey}`),
            }),
        },
        server: {
            apiFeedback: ((base: string) => ({
                root: () => base,
                feedback: {
                    sendFeedback: () => `${base}/feedbacks`,
                },
            }))(`${config.feedbackBaseApiUrl}`),
        },
    };
};

export type Routes = ReturnType<typeof routesForContext>;
